import React, { useEffect, useMemo, useState, useRef, lazy } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ShoppingBag } from 'lucide-react';
import { scanProducts } from '../utils/productScanner';
import { FIRST_NAMES, LAST_INITIALS, US_CITIES, type ScannedProduct } from '../utils/recentPurchaseData';
interface ToastData {
  id: number;
  name: string;
  city: string;
  product: ScannedProduct;
}
function pick<T>(arr: T[]): T {
  return arr[Math.floor(Math.random() * arr.length)];
}
function weightedPick(products: ScannedProduct[]): ScannedProduct {
  const hasWeights = products.some(p => typeof p.weight === 'number');
  if (!hasWeights) return pick(products);
  const total = products.reduce((s, p) => s + (p.weight ?? 1), 0);
  let r = Math.random() * total;
  for (const p of products) {
    r -= p.weight ?? 1;
    if (r <= 0) return p;
  }
  return products[products.length - 1];
}
const FIRST_DELAY_MS = 3000;
const MIN_INTERVAL_MS = 8000;
const MAX_INTERVAL_MS = 15000;
const VISIBLE_MS = 4000;
const NAME_HISTORY_SIZE = 10;
export function RecentPurchase() {
  const [toast, setToast] = useState<ToastData | null>(null);
  const productsRef = useRef<ScannedProduct[]>([]);
  const recentNamesRef = useRef<string[]>([]);
  const idRef = useRef(0);
  const timeoutsRef = useRef<ReturnType<typeof setTimeout>[]>([]);
  const reduceMotion = useMemo(() => {
    if (typeof window === 'undefined') return false;
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }, []);
  useEffect(() => {
    productsRef.current = scanProducts();
    const pickName = () => {
      const history = recentNamesRef.current;
      const candidates = FIRST_NAMES.filter(n => !history.includes(n));
      const pool = candidates.length > 0 ? candidates : FIRST_NAMES;
      const first = pick(pool);
      const initial = pick(LAST_INITIALS);
      const full = `${first} ${initial}`;
      recentNamesRef.current = [first, ...history].slice(0, NAME_HISTORY_SIZE);
      return full;
    };
    const showOne = () => {
      const product = weightedPick(productsRef.current);
      idRef.current += 1;
      const newToast: ToastData = {
        id: idRef.current,
        name: pickName(),
        city: pick(US_CITIES),
        product
      };
      setToast(newToast);
      const hideTimer = setTimeout(() => {
        setToast(null);
      }, VISIBLE_MS);
      timeoutsRef.current.push(hideTimer);
      const nextDelay = VISIBLE_MS + MIN_INTERVAL_MS + Math.random() * (MAX_INTERVAL_MS - MIN_INTERVAL_MS);
      const nextTimer = setTimeout(showOne, nextDelay);
      timeoutsRef.current.push(nextTimer);
    };
    const firstTimer = setTimeout(showOne, FIRST_DELAY_MS);
    timeoutsRef.current.push(firstTimer);
    return () => {
      timeoutsRef.current.forEach(clearTimeout);
      timeoutsRef.current = [];
    };
  }, []);
  return <AnimatePresence data-id="element-121">
      {toast && <motion.div key={toast.id} initial={reduceMotion ? {
      opacity: 0
    } : {
      opacity: 0,
      y: 30,
      scale: 0.98
    }} animate={reduceMotion ? {
      opacity: 1
    } : {
      opacity: 1,
      y: 0,
      scale: 1
    }} exit={reduceMotion ? {
      opacity: 0
    } : {
      opacity: 0,
      y: 30,
      scale: 0.98
    }} transition={{
      duration: reduceMotion ? 0.15 : 0.4,
      ease: [0.21, 0.47, 0.32, 0.98]
    }} role="status" aria-live="polite" className="fixed left-4 right-4 md:left-1/2 md:right-auto md:-translate-x-1/2 md:w-auto md:min-w-[320px] bottom-24 md:bottom-28 z-30 bg-cream/95 backdrop-blur-md border border-ink/5 shadow-soft-lg rounded-xl px-5 md:px-10 py-3 flex items-center gap-3" data-id="element-122">
          <div className="flex-shrink-0 w-12 h-12 rounded-lg bg-sage flex items-center justify-center overflow-hidden p-1" data-id="element-123">
            {toast.product.thumbnailUrl ? <img src={toast.product.thumbnailUrl} alt="" loading="lazy" decoding="async" className="w-full h-full object-contain" data-id="element-124" /> : <ShoppingBag strokeWidth={1.5} className="w-5 h-5 text-ink-light" data-id="element-125" />}
          </div>

          <div className="flex-1 min-w-0 text-left" data-id="element-126">
            <p className="text-xs sm:text-sm text-ink leading-snug" data-id="element-127">
              <span className="font-medium" data-id="element-128">{toast.name}</span>
              <span className="text-ink-light" data-id="element-129"> en {toast.city}</span>
            </p>
            <p className="text-[11px] sm:text-xs text-ink-light leading-snug mt-0.5" data-id="element-130">
              acaba de comprar{' '}
              <span className="text-ink" data-id="element-131">
                {toast.product.editionLabel || toast.product.title}
              </span>
            </p>
            <p className="text-[11px] sm:text-xs font-medium text-bordo leading-snug mt-0.5" translate="no" data-id="element-132">
              <span data-id="element-133">$</span>
              <span data-id="element-134">
                {toast.product.priceNumber.toFixed(2).replace(/\.00$/, '')}
              </span>
            </p>
          </div>
        </motion.div>}
    </AnimatePresence>;
}